<template>
  <div class="auth-form">
    <h1>Регистрация</h1>
    <div class="text" v-if="isSuccessMessageVisible">
      Проверьте почту, мы отправили ссылку для подтверждения. Если письмо не
      пришло, проверьте папку "Спам".
    </div>
    <div class="auth-form-components" v-else>
      <div class="flex flex-col input-labeled">
        <label for="email">E-mail</label>
        <InputText
          id="email"
          v-model="form.email"
          :invalid="invalidEmail || invalid"
        />
      </div>
      <div class="flex flex-col input-labeled">
        <label for="password">Пароль</label>
        <Password
          id="password"
          v-model="form.password"
          :feedback="false"
          toggleMask
          :invalid="invalidPassword"
        />
      </div>
      <div class="flex flex-col input-labeled">
        <label for="fname">Имя</label>
        <InputText id="fname" v-model="form.fname" :invalid="invalid" />
      </div>
      <div class="flex flex-col input-labeled">
        <label for="lname">Фамилия</label>
        <InputText id="lname" v-model="form.lname" :invalid="invalid" />
      </div>
      <div class="flex flex-col input-labeled">
        <label for="organization_name">Название организации</label>
        <InputText
          id="organization_name"
          v-model="form.organization_name"
          :invalid="invalid"
        />
      </div>
      <div class="bad-msg" v-if="errorMessage">{{ errorMessage }}</div>
      <Button
        label="Зарегистрироваться"
        class="form-button"
        @click="signup"
        :disabled="isProgress"
      />
    </div>
    <div class="after-info">
      <div class="forgot">
        <span class="has-account">Есть аккаунт?</span>
        <router-link :to="{ name: 'login' }">Войти</router-link>
      </div>
    </div>
    <div class="fl-senter m-t-5" v-if="isProgress">
      <ProgressSpinner
        aria-label="Loading"
        style="width: 30px; height: 30px"
        strokeWidth="2"
        fill="var(--surface-ground)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth/login";
import { rootGetters } from "@/main";

import InputText from "primevue/inputtext";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import Password from "primevue/password";

const authStore = useAuthStore();
const router = useRouter();
const isProgress = ref(false);
const errorMessage = ref("");
const invalid = ref(false);
const invalidEmail = ref(false);
const invalidPassword = ref(false);
const requestSent = ref(false);

const form = ref({
  email: "",
  password: "",
  fname: "",
  lname: "",
  organization_name: "",
});

const isSuccessMessageVisible = computed(() => requestSent.value);

const redirectIfAuthorized = async () => {
  if (!requestSent.value || !localStorage.getItem("access_token")) return;

  await authStore.getUserInfo();
  const startPage = authStore.getStartPage();
  router.push(startPage);
};

const onStorage = (event: StorageEvent) => {
  if (event.key === "access_token" && event.newValue) {
    redirectIfAuthorized();
  }
};

onMounted(() => {
  window.addEventListener("storage", onStorage);
  document.addEventListener("visibilitychange", redirectIfAuthorized);
});

onBeforeUnmount(() => {
  window.removeEventListener("storage", onStorage);
  document.removeEventListener("visibilitychange", redirectIfAuthorized);
});

function getInvalidEmailMessage(detail: unknown): string | undefined {
  if (!Array.isArray(detail)) return undefined;
  for (const raw of detail) {
    if (!raw || typeof raw !== "object") continue;
    const item = raw as { type?: string; loc?: unknown[] };
    const loc = item.loc;
    const targetsEmail =
      Array.isArray(loc) && loc.some((segment) => segment === "email");
    const isValueError =
      typeof item.type === "string" && item.type.startsWith("value_error");
    if (targetsEmail && isValueError) {
      return "Укажите корректный адрес электронной почты.";
    }
  }
  return undefined;
}

function getPasswordTooShortMessage(detail: unknown): string | undefined {
  if (!Array.isArray(detail)) return undefined;
  for (const raw of detail) {
    if (!raw || typeof raw !== "object") continue;
    const item = raw as {
      type?: string;
      loc?: unknown[];
      ctx?: { min_length?: number };
    };
    const loc = item.loc;
    const targetsPassword =
      Array.isArray(loc) && loc.some((segment) => segment === "password");
    if (!targetsPassword || item.type !== "string_too_short") continue;
    const min = item.ctx?.min_length ?? 8;
    return `Пароль должен содержать не менее ${min} символов.`;
  }
  return undefined;
}

const isFormValid = () => {
  return (
    form.value.email.trim().length > 0 &&
    form.value.password.trim().length > 0 &&
    form.value.fname.trim().length > 0 &&
    form.value.lname.trim().length > 0 &&
    form.value.organization_name.trim().length > 0
  );
};

const signup = () => {
  errorMessage.value = "";
  invalid.value = false;
  invalidEmail.value = false;
  invalidPassword.value = false;

  if (!isFormValid()) {
    invalid.value = true;
    errorMessage.value = "Заполните все поля формы.";
    return;
  }

  isProgress.value = true;

  authStore
    .registerRequest({
      email: form.value.email.trim(),
      password: form.value.password,
      fname: form.value.fname.trim(),
      lname: form.value.lname.trim(),
      organization_name: form.value.organization_name.trim(),
    })
    .then(() => {
      requestSent.value = true;
      redirectIfAuthorized();
    })
    .catch((err) => {
      const status = err.response?.status;
      if (status === 409) {
        errorMessage.value =
          err.response.data?.detail ||
          "Пользователь с таким email или организация уже существует.";
      } else if (status === 422) {
        const detail = err.response?.data?.detail;
        const emailMsg = getInvalidEmailMessage(detail);
        const pwdMsg = getPasswordTooShortMessage(detail);
        if (emailMsg) {
          invalidEmail.value = true;
          errorMessage.value = emailMsg;
        } else if (pwdMsg) {
          invalidPassword.value = true;
          errorMessage.value = pwdMsg;
        } else {
          rootGetters.toast.show(
            "error",
            "Произошла ошибка",
            "Обратитесь к администратору или проверьте соединение с интернетом.",
          );
        }
      } else if (status === 400) {
        const data = err.response?.data;
        if (data?.error_code === "EMAIL_DOMAIN_NOT_ALLOWED") {
          invalidEmail.value = true;
        }
        errorMessage.value =
          data?.detail ||
          "Регистрация с зарубежных почтовых сервисов запрещена. Используйте другой email-адрес.";
      } else {
        rootGetters.toast.show(
          "error",
          "Произошла ошибка",
          "Обратитесь к администратору или проверьте соединение с интернетом.",
        );
      }
    })
    .finally(() => {
      isProgress.value = false;
    });
};
</script>

<style scoped lang="scss">
.auth-form {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50%;
  width: 344px;
}

h1 {
  font-size: 2em;
  font-weight: 600;
  line-height: 142%;
  margin-bottom: 8px;
}

.text {
  margin-top: 8px;
  margin-bottom: 7px;
  font-size: 1em;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

label {
  font-weight: 500;
  font-size: 1em;
  line-height: 160%;
  color: #bcbac2;
  margin-bottom: 4px;
  padding-left: 5px;
}

.input-labeled {
  margin-top: 12px;
  width: 100%;
}

:deep(.p-inputtext.p-component) {
  width: 100%;
  font-size: 14px;
  color: $main-font-color;
  padding: 8px 12px;
}

.form-button {
  margin-top: 19px;
  width: 100%;
}

.bad-msg {
  font-size: 0.8em;
  font-weight: 400;
  line-height: 130%;
  margin-top: 8px;
  color: red;
}

.after-info {
  .forgot {
    margin-top: 8px;
    font-size: 0.8em;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    letter-spacing: -0.2px;

    .has-account {
      color: #b0adb7;
      font-weight: 600;
      padding-right: 5px;
    }

    a {
      color: $main-link-color;
    }
  }
}

.m-t-5 {
  margin-top: 5px;
}

.fl-senter {
  display: flex;
  justify-content: center;
}
</style>
